import { achievementParameterOrder, itemParameterOrder, recipeParameterOrder, skinParameterOrder } from './infoboxOrder.js'

// This serves to convert the internal API properties to infobox properties
const converter = {
	'infusion slot': 'infusionSlot',
	'name': 'wikiName',
	'icon': 'iconName',
	'skin type': 'skinType',
	'description': 'wikiDescription',
	'flavor': 'wikiFlavor',
	'bound': 'binding',
	'skin': 'wikiSkinName',
	'maximum power': 'maxPower',
	'minimum power': 'minPower',
	'collection': 'collections',
	'dye channels': 'dyeChannels',
	'novelty type': 'noveltyType',
	'novelty-id': 'noveltyId',
	'miniature set': 'miniatureSet',
	'miniature id': 'miniatureId',
	'damage type': 'damageType',
	'cap': 'pointCap',
	'decoration id': 'decorationId',
};

const dye_parameters = [
	"color-id", "material", "hue", "kit", "color-cloth", "color-leather", "color-metal", "color-fur"
]

function generateItemInfobox(payload) {
	let output = '';
	if (!payload.itemInfobox) return output;

	output += `{{${payload.itemInfobox.infoboxType} infobox\n`;

	// Begin looping over all the parameters returned by the local api
	for (let param of itemParameterOrder) {
		let content = null;

		// We have a few special cases so let's go over those now

		// Name: we never really output this in item infobox
		// Special cases may apply but no way we figure that out ahead of time
		if (param === 'name') continue;

		// Icon: Only display icon if it's different from the wikiname
		if (param === 'icon') {
			let iconName = payload.itemInfobox.iconName;
			if (!iconName) continue;

			iconName = iconName.replace('.png', '');

			if (iconName === payload.itemInfobox.wikiName) continue;
		}
		
		if (param === 'description') {
			// Skip some cases where we don't want the description
			if (payload.itemInfobox.variables &&
				payload.itemInfobox.wikiDescription == "Double-click to consume.") continue;

			if (payload.itemInfobox.type?.toLowerCase() == "recipe sheet" &&
			payload.itemInfobox.wikiDescription == "Double-click to consume.") continue;

			if (payload.itemInfobox.infoboxType == "Dye") continue;
		}

		if (param === 'level') {
			if (payload.itemInfobox.level === 0) continue;
		}
		
		// Collection: Local api returns an array, make sure it's dealt appropriately
		if (param === 'collection') {
			if (!payload.itemInfobox.collections || payload.itemInfobox.collections.length <= 0) continue;

			content = payload.itemInfobox.collections.map(c => c.name).join('; ');
			//continue;
		}

		// Dye parameters have their own subcattegory in local api
		if (payload.itemInfobox.infoboxType == "Dye" && dye_parameters.includes(param)) {
			// Hex values of colors don't have the `#` on wiki
			if(param.startsWith("color-") && param != "color-id") {
				if (!payload.itemInfobox.color[param]) content = "unknown";
				else content = payload.itemInfobox.color[param].replace("#", "");
			}
			else if (payload.itemInfobox.color[param]) content = payload.itemInfobox.color[param];
			else if(payload.itemInfobox.color[converter[param]]) content = payload.itemInfobox.color[converter[param]];
		}

		if (param == "bonus" && 
		payload.itemInfobox.infoboxType == "Upgrade component" &&
		payload.itemInfobox.bonus) {
			content = '';

			for (let bonus of payload.itemInfobox.bonus) {
				let value = bonus.value.replace("<c=@reminder>", "");
				value = value.replace("</c>", "");
				value = value.replace("</c >", "");

				content += `| bonus${bonus.position} = ${value}\n`
			}
			// Remove last \n
			content = content.slice(0, -1);

			// Just due to the structure of this code where `| bonus =` will be prepended...
			content = content.slice(11);
			param = "bonus1";
		}

		// Skin: If there's a (or multiple) skin, the local api returns an array, so deal with that
		if (param === 'skin' && payload.itemInfobox.wikiSkinName) {
			content = payload.itemInfobox.wikiSkinName.join('; ');
		//	continue;
		}

		if (param === 'skin type'){
			if (payload.itemInfobox.type?.toLowerCase() == "weapon skin" &&
			payload.skinInfoboxes.length > 0) {
				content = payload.skinInfoboxes[0].type;
			}
		}

		if (param === "group" && payload.itemInfobox.infoboxType == 'Decoration') {
			// If it's a homestead decoration
			if (payload.itemInfobox?.categoryNames?.length > 0) {
				content = payload.itemInfobox.categoryNames.join(', ');
			}
			// It's a guild hall decoration
			else {
				content = "<!-- select a type from [[:Category:Decorations]]-->";
			}
		}

		// Deal with uniqueness around decoration type
		if (param === 'type') {
			// Back items and Dye don't display type in wiki
			if (payload.itemInfobox.infoboxType == "Back item") continue;
			if (payload.itemInfobox.infoboxType == "Dye") continue;

			if (payload.itemInfobox.noveltyType) {
				content = "Gizmo, Novelty";
			}

			if (payload.itemInfobox?.type === "Crafting Material") {
				content = "Crafting Material\n| material type = basic";
			}
		}
		// Now we check if we find the infobox parameters in payload and it's not null (or falsy)
		// We also check if the "converted" value is there instead
		if(!content){
			if (payload.itemInfobox[param] || payload.itemInfobox[param] === 0) content = payload.itemInfobox[param];
			else if(payload.itemInfobox[converter[param]]) content = payload.itemInfobox[converter[param]];
		}

		// If we found a property we care about generate the output
		if (param == "gallery") param = "gallery1"

		if (content  || content === 0) output += `| ${param} = ${content}\n`;
		else if (param === 'set') {
			if (payload.itemInfobox.infoboxType == "Trinket") continue;
			if (payload.itemInfobox.set === "") output += '| set = \n';
		}else if (param === 'miniature set' && payload.itemInfobox.miniatureId) {
			output += '| miniature set = \n';
		}
	}
	// Close the infobox
	output += '}}';

	return output;
}

function generateItemAcquisition(payload) {
	const acquisition = payload.acquisition;
	let output = '== Acquisition ==\n';

	if (acquisition.text) return output += acquisition.text;

	let recipe_ids = acquisition.recipeInfobox.map(r => r.id);

	let all_disciplines = '';
	if (acquisition.recipeInfobox.length > 1 && recipe_ids.length == 1) {
		all_disciplines = acquisition.recipeInfobox.map(r => r.disciplines.join(", ")).join(", ");
	}

	for (let i=0; i < recipe_ids.length; i++) {
		if (i > 0) output += "\n";

		// Now we start building the actual recipe
		output += '{{Recipe\n';
		const recipe = acquisition.recipeInfobox[i];
		
		for (const param of recipeParameterOrder) {
			let content = null;

			if (param === 'ingredients') {
				const fetchedIngredients = recipe.wikiIngredients;
				
				// Loop over the ingredients
				for (let i = 0; i < fetchedIngredients.length; i++) {
					if (!fetchedIngredients[i]) continue;
					const ingredient = fetchedIngredients[i];

					output += `| ingredient${i + 1} = ${ingredient.count} ${ingredient.name}\n`;
				}
			} else if (param === 'disciplines') {
				if (all_disciplines != '') {
					content = all_disciplines
				} else {
					content = recipe.disciplines.join(', ');
				}
			}  else {
				// Now we check if we find the infobox parameters in payload and it's not null (or falsy)
				// We also check if the "converted" value is there instead

				if (recipe[param]) content = recipe[param];
				else if(recipe[converter[param]]) content = recipe[converter[param]];
			}

			if (content) output += `| ${param} = ${content}\n`;
		}
		// Close the infobox
		output += '}}';
	}

	return output
}

function generateItemInterwikis(payload, language) {
	const interwikis = payload.interwikis;
	let output = '';

	if (!interwikis) return null;

	// Add all interwikis except the queried language one
	// i.e if it's for en, don't put en interwiki
	for (const [lang, inter] of Object.entries(interwikis)) {
		if (lang === language) continue;
		output += `[[${lang}:${inter}]]\n`;
	}

	// Trim to remove trailing newline
	return output.trim();
}

function generateSkinBox(skinBox, payload) {
	let output = '{{Skin infobox\n';

	for (const param of skinParameterOrder) {
		let content = null;

		if (param === 'collection') {
			// Ensure that we have a collection param in payload
			const collections = payload.itemInfobox.collections;
			if (!collections || collections.length === 0) continue;

			// Ensure that there is at least one entry that depends on the skin
			const skinCollect = collections.filter( c => c.type === 'Skin');
			if (!skinCollect || skinCollect.length === 0) continue;

			// Map and join all achievement names
			content = skinCollect.map(s => s.name).join('; ');
		
		} else if (skinBox[param] || skinBox[param] === 0) content = skinBox[param];
		else if(skinBox[converter[param]]) content = skinBox[converter[param]];
		
		if (content) output += `| ${param} = ${content}\n`;
		else if (param === 'set' && skinBox.set === '') output += '| set = \n';
	}

	output += '}}';

	return output;
}

function generateAchievementInfobox(payload) {
	const infobox = payload.infobox;
	let output = '';
	output += `{{Achievement table row\n`;
	// Begin looping over all the parameters returned by the local api
	for (const param of achievementParameterOrder) {
		let content = null;

		// Special cases will be Tiers, rewards, bits, wikiPrereqs
		if (param === 'tiers') {
			content = infobox.tiers.join('\n');
		}
		// This part feels like a mess but it's basically just cycling through
		// All the reward options. If they exist throw them in an array and join()
		else if (param === 'reward') {
			if (!infobox.rewards ||  infobox.rewards.items.length === 0) continue;
			content = infobox.rewards.items.map(i => i.name).join('; ');
		} else if(param === 'quantity') {
			if (!infobox.rewards ||  infobox.rewards?.items.length === 0) continue;
			content = infobox.rewards.items.map(i => i.count).join('; ');
		} else if (param === 'title') {
			if (!infobox.rewards || infobox.rewards.titles.length === 0) continue;
			content = infobox.rewards.titles.map(i => i.name).join('; ');
		} else if (param === 'mastery') {
			if (!infobox.rewards ||  infobox.rewards.masteries.length === 0) continue;
			content = infobox.rewards.masteries.map(i => i.region).join('; ');
		} else if (param === 'mastery id') {
			if (!infobox.rewards || infobox.rewards.masteries.length === 0) continue;
			content = infobox.rewards.masteries.map(i => i.id).join('; ');
		} else if (param === 'coins') {
			if (!infobox.rewards || infobox.rewards.coins.length === 0) continue;
			content = infobox.rewards.coins.map(i => i.count).join('; ');
		} else if (param === 'objectives') {
			if (!infobox.objectives) continue;
			content = infobox.objectives.join('\n');
		} else if(param === 'collection') {
			if (!infobox.collection) continue;
			content = infobox.collection.map(c => c.name).join('\n');
		} 


		else if (param === 'type') {
			if (!infobox?.flags.includes("Repeatable")) continue;
			content = "repeatable";
		}
		else if (param === 'prerequisite') {
			if (!infobox?.wikiPrereqs) continue;
			if (infobox?.wikiPrereqs.length < 1 || !infobox?.wikiPrereqs[0]?.name) continue;
			content = `${infobox?.wikiPrereqs[0].name} <!--id=${infobox?.wikiPrereqs[0].id}-->`;
		}
		else if (param === "hidden") {
			content = infobox?.hidden == true ? "yes" : "";
		}
		else if (infobox[param]) {
			content = infobox[param];
		}
		else if(infobox[converter[param]]) {
			content = infobox[converter[param]];
		}


		// Small section for 'hidden collection' override
		const categoriesWithHidden = ["Rare Collections", "Black Lion Collections"];
		if (param === 'collection' && categoriesWithHidden.includes(payload.category?.name)) {
			param = 'hidden collection';
		}

		// If we found a property we care about generate the output
		if (content) output += `| ${param} = ${content}\n`;
	}
	// Close the infobox
	output += '}}';

	return output;
}

function generateItemPrefixes(payload) {
	if (!payload.prefixSelection || payload.prefixSelection.length < 1) return null;

	let output = "";
	output += "== Available prefixes ==\n";
	output += "* The following [[Attribute combinations|prefixes]] can be selected:\n";
	output += "{{prefix selection|";

	// Sometimes it's a list of stats, other times it is
	// something like "allDefenders"
	if (Array.isArray(payload.prefixSelection)) {
		output += payload.prefixSelection.sort().join(", ");
	} else {
		const result = payload.prefixSelection.replace(/([A-Z])/g, " $1");
		const finalResult = result.charAt(0).toUpperCase() + result.slice(1);
		output += finalResult;
	}

	output += "|collapsed=y}}";

	return output;
}

function generateNavs(payload) {
	const infobox = payload.itemInfobox;
	let output = "";

	// Weapon skin collections
	if(infobox?.consumableSkinIds &&
		infobox?.consumableSkinIds.length > 0 &&
		infobox.collections?.length == 1) {
		// Remove "weapon collection" from collection name
		let tmp = infobox.collections[0].name.split(" ").slice(0, -2).join(" ")
		// Remove "'s"
		tmp = tmp.replace("\'s", "")
		output += `{{${tmp} weapons nav}}`
	}
	else if (infobox.collections?.length > 0) {
		output += "{{Collection nav}}";
	}

	if (!infobox.type) return output;

	// Other navs
	if (infobox.type?.toLowerCase() == "gathering") {
		output += "{{Unlimited gathering tool nav|collapsed=y}}";
	}
	if (infobox?.type == "Decoration") {
		output += "{{<Type> Decorations nav|<subtype>}}";
	}
	if (infobox.type?.toLowerCase() == "bag") {
		output += "{{Bags nav}}";
	}
	if (infobox.type?.toLowerCase() == "lure") {
		output += "{{Lures nav}}";
	}

	if (infobox.infoboxType == "Decoration") {
		if (infobox.type?.toLowerCase() == "homestead") {
			output += `<!--remove or fix content in < ... >\n` + 
			`{{Homestead <${infobox.categoryNames.join(" ")}> decorations nav<|subtype if any>}}\n` +
			`-->`;
		} else if (infobox.type?.toLowerCase() == "guild hall") {
			output += `<!--remove or fix content in < ... > using Category:Decoration_navigation_templates\n` + 
			`{{<replace me> decorations nav<|subtype if any>}}\n` +
			`-->`;
		}
	}

	return output;
}

function generateBoxHeader(payload, type) {


	// Let's define some of the stuff we need to not have weird defs mid output generation
	let name = {
		'items': payload.itemInfobox?.wikiName,
		'achievements': payload.infobox?.name,
		'homestead/decorations': payload.itemInfobox?.wikiName,
	}[type];
	let id = {
		'items': payload.itemInfobox?.id,
		'achievements': payload.infobox?.id,
		'homestead/decorations': payload.itemInfobox?.decorationId,
	}[type];
	let achievCategory = payload.category?.name || null;

	const skindIds = payload?.skinInfoboxes?.map(s => s.id) || null;
	let pageExists = payload.pageExists || null;

	let output = '<p style="margin: 0;margin-bottom: 2px;">';
	
	// If the page exists, output the "go to" and "edit" links
	// If it doesn't, show only a red "create page" option which brings straight to edit.
	// Note that "page exists" is true if it exists, or we just don't know
	// The term is not ideal but better than `pageExistsOrUncertainButIWantADefault`
	if (pageExists) {
		output += `<a href="http://wiki.guildwars2.com/wiki/${encodeURI(name)}">wiki link</a>`;
		output += ` &#8226 <a href="http://wiki.guildwars2.com/index.php?title=${encodeURI(name)}&action=edit">edit wiki link</a>`;
	} else {
		output += `<a href="http://wiki.guildwars2.com/index.php?title=${encodeURI(name)}&action=edit" style='color: rgb(255, 0, 0);'>Create new page</a>`;
	}
	
	// If there is an achievement category found, create a link for it as achiev boxes often go on that page instead.
	if (achievCategory) {
		output += ` &#8226 <a href="http://wiki.guildwars2.com/index.php?title=${encodeURI(achievCategory)}&action=edit">edit wiki category link</a>`;
	}

	// Links to actual api page, and skin api page if relevant
	output += ` &#8226 <a href='https://api.guildwars2.com/v2/${type}/${id}'>API link</a>`;
	if (skindIds) {
		output += ` &#8226 <a href='https://api.guildwars2.com/v2/skins?ids=${skindIds.join(",")}'>Skin API link</a>`;
	}
	output += ` &#8226 <a href='${payload.url}'>ezAPI</a>`;

	output += '</p>';
	output += `${name}<br/>`;
	if (achievCategory) {
		output += `Achievement category: ${achievCategory}<br/>`;
	}


	return output
}

function generateTeaches(payload) {

	if (!payload.itemInfobox?.type) return '';

	if (payload.itemInfobox.type != "recipe sheet") return '';

	return "== Teaches recipes ==\n{{Teaches recipe}}";

}

function generateItemIntro(payload) {
	
	let craftName = payload.itemInfobox.wikiName.split(": ")[1];
	if (payload.itemInfobox.type == "recipe sheet") {
		return `[[${payload.itemInfobox.wikiName}]] is a [[recipe sheet]] used to learn the recipe to craft [[${craftName}]].`;
	}

	if (payload.itemInfobox.type == "homestead") {
		return `[[${payload.itemInfobox.wikiName}]] is a [[homestead]] [[decoration]] crafted using the [[handiworker]] discipline.`;
	}
}

function generateItemContents(payload) {
	if (!payload.hasOwnProperty("hardstuckContents")) return "";
	if (Object.keys(payload["hardstuckContents"]).length === 0) return "";

	// Static section headers
	let output = "== Contents ==\n";
	output += "{{section-stub|item|Please verify and add missing items}}\n";

	// Loop over all rewards and wiki format
	for (const reward of Object.values(payload["hardstuckContents"])) {
		output += `* {{contains|${reward["en_name"]}|qty=${reward["count"]}|chance=Guaranteed}}\n`;
	}


	return output;


}

export function formatAchievementInfobox(payload, language) {
	let output = generateBoxHeader(payload, 'achievements');

	output += "<textarea rows='15' cols='50'>";
	output += generateAchievementInfobox(payload);
	output += '</textarea><br/>';
	return output;
}

export function formatItemInfobox(payload, language) {
	//if(payload.Error) return "";

	const infoboxComponents = [
		generateItemInfobox(payload), generateItemIntro(payload), generateItemAcquisition(payload),
		generateTeaches(payload), generateItemContents(payload), generateItemPrefixes(payload),
		generateNavs(payload), generateItemInterwikis(payload, language)
	]

	let output = '';
	// Some slight faffery due to homestead decorations
	if (payload.itemInfobox.type == "homestead") output = generateBoxHeader(payload, 'homestead/decorations');
	else output = generateBoxHeader(payload, 'items');

	output += "<textarea rows='15' cols='50'>";

	for (const component of infoboxComponents) {
		if (component) output += `${component}\n\n`;
	}

	output = output.trim();
	output += '</textarea><br/>';
	
	return output;
}

function buildSkinIntro(payload, skinBox, idx) {
	let wikiPageName = payload.itemInfobox.wikiSkinName;
	if (Array.isArray(payload.itemInfobox.wikiSkinName)) wikiPageName = wikiPageName[idx];

	let name = skinBox.name || payload.itemInfobox.wikiSkinName;
	if (skinBox.isBLArmor === true) name = `${wikiPageName}|${name}`;

	const itemType = payload.itemInfobox.type?.toLowerCase() === 'weapon skin' ? skinBox.type : payload.itemInfobox.type;
	let output = '';
	
	// Deal with diverging armor skin description
	if (skinBox.type == "Back") {
		output += `\n\n[[${name}]] is a back item [[skin]].\n\n`;
	} else if (itemType === "armor skin") {
		// .... is a <light> <chest> skin
		output += `\n\n[[${name}]] is a ${skinBox.class?.toLowerCase()} ${skinBox.type?.toLowerCase()} [[skin]].\n\n`;
	} else if (payload.itemInfobox.skinType == "Fishing Rod") {
		output += `\n\n[[${name}]] is a [[fishing rod]] [[skin]].\n\n`;
	} else if (name == payload.itemInfobox.apiName) {
		name = `${name} (skin)|${name}`;
		output += `\n\n[[${name}]] is a ${itemType} [[skin]].\n\n`;
	}
	else {
		output += `\n\n[[${name}]] is a ${itemType} [[skin]].\n\n`;
	}

	return output;
}


export function formatSkinBox(payload) {
	if (!payload.skinInfoboxes) return null;

	let output = '';
	let i = 0;
	for (const skinBox of payload.skinInfoboxes) {
		output += '<textarea rows=\'15\' cols=\'50\'>';
		if (skinBox.isBLArmor) {
			output += `{{otheruses|the [[skin]] unlocked in the [[wardrobe]]|the consumable item|${payload.itemInfobox.wikiName}}}\n`;
		}

		output += generateSkinBox(skinBox, payload);
		output += buildSkinIntro(payload, skinBox, i);
		
		// TODO: Add the sentence `that is part of the <x> <weapon/armor> set.` if applicable
		output += '== Acquisition ==\n{{skin list}}\n\n';
		i++;
		output = output.trim();
		output += '</textarea><br/>';
	}
	

	return output;
}

export function formatIconList(payload) {
	const duplicateIcons = payload.itemInfobox.duplicateIcons

	let output = `<div id='icon'><a href='${payload.itemInfobox.iconLink}'><img src='${payload.itemInfobox.iconLink}'></a>`;

	if (!duplicateIcons || duplicateIcons.length === 0) return output;

	output += "<p>This icon is a duplicate of: <ul align='right'>";

	for (const icon of duplicateIcons) {
		output += `<li><a href="http://wiki.guildwars2.com/wiki/${encodeURI(icon.name)}">${icon.name}</a></li>`;
	}
	output += '</ul></p></div>';

	return output
}
